import { h } from 'preact'
import { useState } from 'preact/hooks'
import FormInput from '../elements/FormInput'
import initializeForm from '../utilities/initializeForm'
import { formFieldGroups, formSchema, initialValues } from './config'

import sharedStyles from '../sharedStyles.module.scss'
import formatNumber from '../../../utilities/formatNumber'

import ChartContainer from '../../charts/ChartContainer/ChartContainer'
import LineGraph from '../../charts/LineGraph/LineGraph'

export default function NetWorthCalculator({ formName }) {
  const { formValues, formErrors, handleInputChange, validateForm, resetForm } =
    initializeForm({
      initialValues,
      formSchema,
    })

  const [view, setView] = useState('form')
  const [data, setData] = useState(null)

  function renderGroupInputs(groupID) {
    return Object.entries(formFieldGroups[groupID]).map(([key, label]) => {
      const id = `${formName}-${groupID}-${key}`
      const formKey = `${groupID}-${key}`
      return (
        <FormInput
          key={id}
          id={id}
          identifier={formKey}
          input={{
            label: label,
            class: 'Currency',
            type: 'text',
          }}
          error={formErrors?.[formKey]}
          value={formValues?.[formKey]}
          handleInputChange={handleInputChange}
        />
      )
    })
  }

  function calculateGroupTotal(groupId) {
    return Object.keys(formFieldGroups[groupId]).reduce((num, key) => {
      const valueKey = `${groupId}-${key}`
      if (formValues[valueKey]) {
        return (num += Number(formValues[valueKey].replace(/[^0-9.-]/g, '')))
      }
      return num
    }, 0)
  }

  function renderDisplayTotal(total) {
    return isNaN(total)
      ? 'One of the fields above is not a number.'
      : Number(total)
      ? `$${formatNumber(total)}`
      : null
  }

  const cashTotal = calculateGroupTotal('cash')
  const otherAssetsTotal = calculateGroupTotal('otherAssets')
  const investmentTotal = calculateGroupTotal('investments')
  const insuranceTotal = calculateGroupTotal('insurance')
  const liabilityTotal = calculateGroupTotal('liabilities')

  function submit() {
    validateForm().then(() => {
      const netWorthVal = Number(
        cashTotal + otherAssetsTotal + investmentTotal - liabilityTotal,
      )

      const min = Math.min(0, netWorthVal, Number(insuranceTotal))
      console.log(min)
      setData({
        xRange: [0, 1],
        yRange: [
          min,
          netWorthVal > Number(insuranceTotal)
            ? netWorthVal
            : Number(insuranceTotal),
        ],
        data: [
          {
            key: 'netWorth',
            max: Number(
              cashTotal + otherAssetsTotal + investmentTotal - liabilityTotal,
            ),
            fill: '#035595',
            label: 'Net Worth',
            rotate: true,
            value: [
              {
                i: 0,
                val: 0,
              },
              {
                i: 1,
                val: netWorthVal,
              },
              {
                i: 1,
                val: 0,
              },
              {
                i: 0,
                val: 0,
              },
            ],
          },
          {
            key: 'lifeInsurance',
            max: Number(insuranceTotal),
            fill: '#a9ddf7',
            label: 'Life Insurance',
            value: [
              {
                i: 0,
                val: 0,
              },
              {
                i: 1,
                val: Number(insuranceTotal),
              },
              {
                i: 1,
                val: 0,
              },
              {
                i: 0,
                val: 0,
              },
            ],
          },
        ].sort((a, b) => (Number(a.max) < Number(b.max) ? 1 : -1)),
      })

      setView('results')
    })
  }

  if (view === 'results') {
    return (
      <ChartContainer
        edit={() => setView('form')}
        reset={() => {
          setView('form')
          setData(null)
          resetForm()
        }}
      >
        <LineGraph {...data} />
      </ChartContainer>
    )
  }

  return (
    <div>
      <form
        className={sharedStyles.Form}
        onSubmit={e => {
          e.preventDefault()
          submit()
        }}
      >
        <h2>Assets</h2>
        <h4>Cash and Bank Accounts</h4>
        {renderGroupInputs('cash')}

        <div className={sharedStyles.SectionTotal}>
          <p>Total cash and bank accounts: </p>
          <p>{renderDisplayTotal(cashTotal)}</p>
        </div>

        <h4>Other Assets</h4>
        {renderGroupInputs('otherAssets')}
        <div className={sharedStyles.SectionTotal}>
          <p>TOTAL Other Assets:</p>
          <p>{renderDisplayTotal(otherAssetsTotal)}</p>
        </div>

        <h4>Investments</h4>
        {renderGroupInputs('investments')}

        <div className={`${sharedStyles.SectionTotal}`}>
          <p>TOTAL Investments:</p>
          <p>{renderDisplayTotal(investmentTotal)}</p>
        </div>

        <div className={sharedStyles.SectionTotal}>
          <p>TOTAL Assets (A):</p>
          <p>
            {renderDisplayTotal(cashTotal + otherAssetsTotal + investmentTotal)}
          </p>
        </div>
        <h4>Life Insurance</h4>
        {renderGroupInputs('insurance')}
        <div className={sharedStyles.SectionTotal}>
          <p>TOTAL Life Insurance (B):</p>
          <p>{renderDisplayTotal(insuranceTotal)}</p>
        </div>
        <h2>Liabilities</h2>
        {renderGroupInputs('liabilities')}

        <div className={`${sharedStyles.SectionTotal}`}>
          <p>TOTAL Liabilities (C)</p>
          <p>{renderDisplayTotal(liabilityTotal)}</p>
        </div>

        <div className={`${sharedStyles.SectionTotal}`}>
          <p>TOTAL Net Worth (A-C):</p>
          <p>
            {renderDisplayTotal(
              cashTotal + otherAssetsTotal + investmentTotal - liabilityTotal,
            )}
          </p>
        </div>

        <div className={`${sharedStyles.SectionTotal}`}>
          <p>TOTAL Net Worth at Death ((A+B)-C):</p>
          <p>
            {renderDisplayTotal(
              cashTotal +
                otherAssetsTotal +
                investmentTotal +
                insuranceTotal -
                liabilityTotal,
            )}
          </p>
        </div>
        <div className={sharedStyles.SubmitContainer}>
          <button type="submit" className="button dark">
            Calculate
          </button>
        </div>
      </form>
    </div>
  )
}
